import { useState } from 'react';
import { t } from '../../i18n/index.js';
import { Dialog, DialogContent } from '../../ui/Dialog.js';
import { Button } from '../../ui/Button.js';
import { Banner } from '../../ui/Banner.js';
import { Field } from '../../ui/Field.js';

/**
 * Creating an account.
 *
 * ══════════════════════════════════════════════════════════════════════════
 * THE SLUG IS THE FIELD THAT MATTERS
 * ══════════════════════════════════════════════════════════════════════════
 *
 * It appears in URLs, and more importantly it is what an operator types to
 * confirm a purge. A slug with a space or a capital in it makes that
 * confirmation ambiguous at exactly the moment nobody wants ambiguity, so it
 * is constrained here and again in the platform.
 *
 * Suggested from the name rather than left blank: a person filling this in
 * has already decided what the account is called, and making them transcribe
 * it in a second format is how you get "Fresh-Cart" and "freshcart-2". They
 * can still edit it — the moment they do, it stops following the name, because
 * a field that keeps overwriting what somebody typed is worse than one that
 * never helped.
 */
const PLANS = ['native', 'control'] as const;

function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50);
}

export function CreateTenantDialog({
  onCancel,
  onCreate,
}: {
  onCancel: () => void;
  onCreate: (input: {
    slug: string;
    name: string;
    planCode: string;
    ownerEmail: string;
    ownerName?: string;
  }) => Promise<void>;
}) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEdited, setSlugEdited] = useState(false);
  const [planCode, setPlanCode] = useState<string>('native');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [ownerName, setOwnerName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [working, setWorking] = useState(false);

  const effectiveSlug = slugEdited ? slug : slugify(name);
  const ready = effectiveSlug.length >= 3 && name.trim() !== '' && ownerEmail.trim() !== '';

  return (
    <Dialog open onOpenChange={(open) => !open && onCancel()}>
      <DialogContent
        title={t('tenants.createTitle')}
        description={t('tenants.createBody')}
        width="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="secondary" onClick={onCancel} disabled={working}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={!ready || working}
              onClick={() => {
                setError(null);
                setWorking(true);
                void onCreate({
                  slug: effectiveSlug,
                  name: name.trim(),
                  planCode,
                  ownerEmail: ownerEmail.trim(),
                  ownerName: ownerName.trim() || undefined,
                })
                  .catch((err: unknown) => {
                    setError(err instanceof Error ? err.message : t('tenants.createFailed'));
                  })
                  .finally(() => setWorking(false));
              }}
            >
              {working ? t('tenants.creating') : t('tenants.create')}
            </Button>
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          {error ? (
            <Banner intent="danger" title={t('tenants.createFailedTitle')}>
              {error}
            </Banner>
          ) : null}

          <Field label={t('tenants.accountName')} hint={t('tenants.accountNameHint')} required>
            {(field) => (
              <input
                {...field}
                type="text"
                value={name}
                autoComplete="off"
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-(--radius-control) border border-subtle bg-canvas px-3 py-2 text-md text-primary outline-none focus-visible:border-focus"
              />
            )}
          </Field>

          <Field label={t('tenants.slug')} hint={t('tenants.slugHint')} required>
            {(field) => (
              <input
                {...field}
                type="text"
                value={effectiveSlug}
                autoComplete="off"
                spellCheck={false}
                onChange={(e) => {
                  setSlugEdited(true);
                  setSlug(e.target.value);
                }}
                className="w-full rounded-(--radius-control) border border-subtle bg-canvas px-3 py-2 font-mono text-md text-primary outline-none focus-visible:border-focus"
              />
            )}
          </Field>

          <Field label={t('tenants.plan')} hint={t('tenants.planHint')} required>
            {(field) => (
              <select
                {...field}
                value={planCode}
                onChange={(e) => setPlanCode(e.target.value)}
                className="w-full rounded-(--radius-control) border border-subtle bg-canvas px-3 py-2 text-md text-primary outline-none focus-visible:border-focus"
              >
                {PLANS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            )}
          </Field>

          <Field label={t('tenants.ownerEmail')} hint={t('tenants.ownerEmailHint')} required>
            {(field) => (
              <input
                {...field}
                type="email"
                value={ownerEmail}
                autoComplete="off"
                onChange={(e) => setOwnerEmail(e.target.value)}
                className="w-full rounded-(--radius-control) border border-subtle bg-canvas px-3 py-2 text-md text-primary outline-none focus-visible:border-focus"
              />
            )}
          </Field>

          <Field label={t('tenants.ownerName')} hint={t('tenants.ownerNameHint')}>
            {(field) => (
              <input
                {...field}
                type="text"
                value={ownerName}
                autoComplete="off"
                onChange={(e) => setOwnerName(e.target.value)}
                className="w-full rounded-(--radius-control) border border-subtle bg-canvas px-3 py-2 text-md text-primary outline-none focus-visible:border-focus"
              />
            )}
          </Field>
        </div>
      </DialogContent>
    </Dialog>
  );
}
